import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import api from '../utils/api';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Description as FormIcon,
  Assignment as SubmissionIcon,
  People as PeopleIcon,
  BarChart as ChartIcon
} from '@mui/icons-material';

const Dashboard = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    forms: 0,
    submissions: 0,
    users: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [formsRes, submissionsRes, usersRes] = await Promise.all([
        api.get('/forms?limit=1'),
        isAdmin() ? api.get('/submissions?limit=1') : Promise.resolve({ data: { pagination: { total: 0 } } }),
        isAdmin() ? api.get('/admin/users?limit=1') : Promise.resolve({ data: { pagination: { total: 0 } } })
      ]);

      setStats({
        forms: formsRes.data.pagination.total,
        submissions: submissionsRes.data.pagination?.total || 0,
        users: usersRes.data.pagination?.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    {
      title: t('nav.forms'),
      value: stats.forms,
      icon: <FormIcon sx={{ fontSize: 60 }} />,
      color: '#1976d2',
      path: '/forms',
      roles: ['admin', 'field_user']
    },
    {
      title: t('nav.submissions'),
      value: stats.submissions,
      icon: <SubmissionIcon sx={{ fontSize: 60 }} />,
      color: '#2e7d32',
      path: '/submissions',
      roles: ['admin']
    },
    {
      title: t('nav.users'),
      value: stats.users,
      icon: <PeopleIcon sx={{ fontSize: 60 }} />,
      color: '#ed6c02',
      path: '/users',
      roles: ['admin']
    }
  ];

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h4" gutterBottom>
        {t('nav.dashboard')}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {cards
          .filter(card => !card.roles || card.roles.includes(isAdmin() ? 'admin' : 'field_user'))
          .map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardActionArea onClick={() => navigate(card.path)}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="text.secondary" gutterBottom>
                          {card.title}
                        </Typography>
                        <Typography variant="h3" component="div">
                          {card.value}
                        </Typography>
                      </Box>
                      <Box sx={{ color: card.color }}>
                        {card.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isAdmin()
            ? 'Use the sidebar to manage forms, view submissions, and manage users.'
            : 'Use the sidebar to view available forms and fill out surveys.'}
        </Typography>
      </Box>
    </Layout>
  );
};

export default Dashboard;
